import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { fetchIndicatorScan } from "@/lib/api";

const LS_KEY = "gamma_trade_kce_seen";
const POLL_MS = 5 * 60 * 1000; // 5 min
const FIRST_LOAD_DELAY_MS = 8 * 1000; // wait 8s after mount before first check

const sigKey = (s) =>
  `${s.symbol}:${s.signal}:${s.confidence}:${s.layers?.canopy || ""}:${s.layers?.launch_vector || ""}`;

const loadSeen = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
};

const saveSeen = (set) => {
  try {
    // Cap to 200 most-recent keys
    const arr = Array.from(set).slice(-200);
    localStorage.setItem(LS_KEY, JSON.stringify(arr));
  } catch {
    /* ignore */
  }
};

/**
 * Pro-only background poller. Pings KCE scan every 5 minutes and fires a
 * sonner toast for each *new* long/short signal that wasn't in the seen-set.
 *
 * On the very first run after install, we seed the seen-set silently so users
 * don't get a flood of "old" signals on day-one.
 */
export function useKCESignalsPoll({ enabled }) {
  const seenRef = useRef(loadSeen());
  const seededRef = useRef(seenRef.current.size > 0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const data = await fetchIndicatorScan("kce", { only_active: true });
        if (cancelled) return;
        const results = data?.results || [];
        if (!seededRef.current) {
          // First run ever — seed silently
          results.forEach((s) => seenRef.current.add(sigKey(s)));
          saveSeen(seenRef.current);
          seededRef.current = true;
          return;
        }
        const fresh = [];
        results.forEach((s) => {
          const k = sigKey(s);
          if (!seenRef.current.has(k)) {
            fresh.push(s);
            seenRef.current.add(k);
          }
        });
        if (fresh.length) {
          saveSeen(seenRef.current);
          fresh.slice(0, 3).forEach((s) => {
            const isLong = s.signal === "long";
            const arrow = isLong ? "▲" : "▼";
            const label = isLong ? "LONG" : "SHORT";
            toast(`${arrow} KCE ${label} · ${s.symbol}`, {
              description: (s.rationale && s.rationale[0]) || "All three layers aligned.",
              duration: 8000,
              action: {
                label: "View",
                onClick: () => { window.location.href = "/signals"; },
              },
            });
          });
          if (fresh.length > 3) {
            toast(`+${fresh.length - 3} more KCE signals`, {
              description: "Open the Signals page to review",
              duration: 6000,
            });
          }
        }
      } catch {
        /* swallow — likely auth/network blip */
      }
    };

    const firstId = setTimeout(tick, FIRST_LOAD_DELAY_MS);
    const intervalId = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(firstId);
      clearInterval(intervalId);
    };
  }, [enabled]);
}
